import React from 'react';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { useNotifications } from '../../../hooks/useNotifications';

export function NotificationsConfig() {
  const {
    isSupported,
    hasPermission,
    isSubscribed,
    isLoading,
    enableNotifications,
    disableNotifications,
    testNotification,
  } = useNotifications();

  // Navegador não suporta notificações
  if (!isSupported) {
    return (
      <Card className="border-yellow-200 dark:border-yellow-800 bg-yellow-50/50 dark:bg-yellow-900/10">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <BellOff className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            <CardTitle className="text-base">Notificações Não Disponíveis</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Seu navegador não suporta notificações push. Use Chrome, Firefox, Edge ou Safari atualizado.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            <CardTitle className="text-base">Notificações Push</CardTitle>
          </div>
          {isSubscribed && (
            <Badge variant="default" className="bg-green-600">
              Ativo
            </Badge>
          )}
        </div>
        <CardDescription className="text-sm">
          Receba lembretes automáticos para marcar seus horários
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status */}
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between p-2 rounded bg-muted/50">
            <span className="text-muted-foreground">Horários dos lembretes:</span>
            <span className="font-medium">14:00 e 15:45</span>
          </div>
          <div className="flex items-center justify-between p-2 rounded bg-muted/50">
            <span className="text-muted-foreground">Quando recebo:</span>
            <span className="font-medium">Quando não marcar horários</span>
          </div>
        </div>

        {/* Ações */}
        <div className="flex flex-col gap-2">
          {!isSubscribed ? (
            <Button
              onClick={enableNotifications}
              disabled={isLoading}
              className="w-full"
              size="sm"
            >
              <BellRing className="w-4 h-4 mr-2" />
              {isLoading ? 'Habilitando...' : 'Habilitar Notificações'}
            </Button>
          ) : (
            <>
              <Button
                onClick={testNotification}
                variant="outline"
                disabled={isLoading}
                className="w-full"
                size="sm"
              >
                <BellRing className="w-4 h-4 mr-2" />
                Testar Notificação
              </Button>
              <Button
                onClick={disableNotifications}
                variant="destructive"
                disabled={isLoading}
                className="w-full"
                size="sm"
              >
                <BellOff className="w-4 h-4 mr-2" />
                {isLoading ? 'Desabilitando...' : 'Desabilitar Notificações'}
              </Button>
            </>
          )}
        </div>

        {/* Informações */}
        {!hasPermission && (
          <div className="p-3 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <p className="text-xs text-blue-800 dark:text-blue-200">
              💡 <strong>Primeira vez?</strong> Ao habilitar, você precisará autorizar o navegador a enviar notificações.
            </p>
          </div>
        )}

        {isSubscribed && (
          <div className="p-3 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
            <p className="text-xs text-green-800 dark:text-green-200">
              ✅ Você receberá lembretes automáticos às 14h e 15:45 se não marcar seus horários.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
